import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { calendarDay, shortDate, toFixedHours } from '@app/utils';
import { Roles, TimeValueType } from '@app/utils/types';
import { ReportEntity, useListProducts, useListRoles, useReportSummary, validationSchemas } from '@data';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { AddReportFormValues, ProductStatus, MembershipStatus } from '@data/utils/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { Textarea } from '@ui/components/ui/textarea';
import { MultiSelectField, matchesLabel } from '@ui/components/ui/multi-select';
import { cn } from '@ui/lib/utils';
import { useParams } from 'react-router-dom';
import { useOrganizationId } from '@app/utils/hooks';
import { useOrganization, useOrganizationChapters } from '@data/hooks/organization';
import { sheetFooterClass, tabClass } from '@app/constants/styles';
import { TabBar } from '@app/components/core/tab-bar';
import ReportIncidentForm from '@app/components/shared/phillReport/ReportIncidentForm';
import { resolveAutoFilledRole } from './resolveAutoFilledRole';
import { chapterOptions } from './chapterOptions';
import { WorkdayDateField } from './WorkdayDateField';

// The form holds '' for an empty choice, MultiSelectField an empty array.
const selectedValue = (v: string | undefined): string[] => (v ? [v] : []);
const syncSelectOnChange = (onChange: (v: string) => void) => (next: string[]) => onChange(next[0] ?? '');

export interface ReportModalProps {
  title: string;
  onHide: () => void;
  show?: boolean;
  initialData?: Partial<ReportEntity> | null;
  userId?: string;
  productId?: string;
  onSubmit: (values: Partial<ReportEntity>) => void;
  // The Incident tab's write. Left out where a page has no business writing incident work, and the tabs go.
  onSubmitIncident?: (values: Partial<ReportEntity>) => Promise<unknown>;
  disabledDays?: { before?: Date; after?: Date };
  date?: string;
  reportProductIds?: string[];
}

const ReportModal = ({
  title,
  onHide,
  show = false,
  initialData,
  userId,
  productId,
  onSubmit,
  onSubmitIncident,
  disabledDays,
  date,
  reportProductIds,
}: ReportModalProps): ReactElement => {
  const { t } = useTranslation();
  const [workdayPickerOpen, setWorkdayPickerOpen] = useState(false);
  /** Which kind of entry is being written. Only offered while adding, and only where both can be. */
  const [tab, setTab] = useState<'work' | 'incident'>('work');
  const tabsShown = !!onSubmitIncident && !initialData;
  // A floor, not a fixed height, so switching tabs changes the questions and not the window.
  const panelBody = tabsShown ? 'min-h-[328px]' : undefined;

  const organizationIdFromPath = useOrganizationId();
  const { productId: urlProductId } = useParams() as { productId?: string };
  const organizationId = organizationIdFromPath;

  const initialValues = useMemo(() => {
    const initialWorkday = initialData?.workday || date;

    const initialValuesHours = initialData?.minutes ? toFixedHours(initialData.minutes / 60) : TimeValueType.fullDay;

    return {
      userId: userId || initialData?.involvement?.membership?.user?.id || '',
      productId: initialData?.involvement?.product?.id || productId || urlProductId || '',
      role: initialData?.involvement?.role || '',
      today: initialData?.today || '',
      customHours:
        initialValuesHours === TimeValueType.fullDay || initialValuesHours === TimeValueType.halfDay
          ? initialValuesHours
          : null,
      hours:
        initialValuesHours !== TimeValueType.fullDay && initialValuesHours !== TimeValueType.halfDay
          ? initialValuesHours
          : null,
      workday: calendarDay(initialWorkday ?? new Date()),
    };
  }, [initialData, userId, date]);

  const {
    control,
    handleSubmit: handleUseFormSubmit,
    reset,
    setValue,
    formState: { errors },
    clearErrors,
    watch,
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchemas.ReportSchema),
  });

  useEffect(() => {
    setWorkdayPickerOpen(false);
    if (show) {
      // Every opening starts on the ordinary entry, which is what nearly every one of them is.
      setTab('work');
      reset(initialValues);
    }
  }, [show]);

  const formCustomHours = watch('customHours');
  const formWorkday = watch('workday');
  const formProductId = watch('productId');
  const formUserId = watch('userId');
  const formRole = watch('role');

  const { data: organization = { memberships: [] } } = useOrganization(organizationId);
  const members = organization.memberships.filter(
    x => x.user.status === MembershipStatus.active && x.role !== Roles.client,
  );

  const { data: products, isLoading: isProductsLoading } = useListProducts(organizationId, {
    statuses: [ProductStatus.active, ProductStatus.support],
  });

  const productsOptions = useMemo(
    () =>
      products?.items
        ?.filter(el => !reportProductIds?.includes(el.id))
        .map(product => ({ value: product.id, label: product.name })) || [],
    [products, reportProductIds],
  );

  useEffect(() => {
    if (!productId && productsOptions.length && !initialData) {
      const defaultProduct = productsOptions[0];
      if (defaultProduct) {
        setValue('productId', defaultProduct.value);
      }
    }
  }, [productsOptions, show]);

  const {
    data: chapters,
    isLoading: chaptersIsLoading,
    isError: chaptersIsError,
    refetch: refetchChapters,
  } = useOrganizationChapters(organizationId);

  // Used to pre-select the user's last involvement on the chosen product when starting a new
  // entry — so admins / PMs filling several days in a row don't have to re-pick the role each
  // time. `isMostRecentInvolvement` is computed server-side from the user's previous reports.
  const { data: rolesForRecent } = useListRoles({ userId: formUserId, productId: formProductId });
  const mostRecentRole = rolesForRecent?.items.find(r => r.isMostRecentInvolvement)?.role;

  // Remembers the value we last auto-filled so switching the product or user refreshes the
  // suggestion, while a role the user picked manually is left untouched.
  const autoFilledRoleRef = useRef<string | null>(null);

  useEffect(() => {
    const nextRole = resolveAutoFilledRole({
      isEditing: !!initialData,
      currentRole: formRole,
      lastAutoFilledRole: autoFilledRoleRef.current,
      mostRecentRole,
    });
    if (nextRole === null) return;
    setValue('role', nextRole);
    autoFilledRoleRef.current = nextRole;
  }, [initialData, formRole, mostRecentRole, setValue]);

  const rolesOptions = useMemo(
    () => chapterOptions(chapters?.items ?? [], initialData?.involvement?.role),
    [chapters, initialData],
  );
  const showRoles = !chaptersIsError && (chaptersIsLoading || rolesOptions.length > 0);

  const memberOptions = useMemo(
    () =>
      members
        .filter(m => !!m.user.id)
        .map(m => ({ value: m.user.id as string, label: `${m.user.firstName} ${m.user.lastName}` })),
    [members],
  );

  const handleSubmit: SubmitHandler<AddReportFormValues> = values => {
    const { hours, customHours, ...restValues } = values;
    onSubmit({
      ...(initialData || {}),
      ...restValues,
      hours: customHours || hours || 0,
    });
  };

  const { mutate: fetchSummaryData, isPending: isLoadingSummary } = useReportSummary();

  const generateSummary = async () => {
    const productId = watch('productId');
    const workday = watch('workday') ?? date;
    const since = new Date(workday!).toISOString();
    const until = new Date(workday!);
    until.setHours(26);

    fetchSummaryData(
      {
        productId,
        since,
        until: until.toISOString(),
      },
      {
        onSuccess: data => {
          setValue('today', data);
        },
      },
    );
  };

  const selectedProductLabel = productsOptions.find(el => el.value === formProductId)?.label;

  return (
    <Dialog open={show} onOpenChange={open => !open && onHide()}>
      <DialogContent className="sm:max-h-[calc(100vh-4rem)] sm:max-w-[600px] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {/* Only while writing a new entry: an existing one is already one kind or the other. Two kinds of
            entry, not two windows, so the day, the product and anything written carry across. */}
        {tabsShown ? (
          <TabBar activeKey={tab}>
            {(['work', 'incident'] as const).map(kind => (
              <button
                key={kind}
                type="button"
                aria-pressed={kind === tab}
                className={tabClass(kind === tab)}
                onClick={() => setTab(kind)}
              >
                {t(kind === 'work' ? 'reportModal.standardWork' : 'reportModal.incidentWork')}
              </button>
            ))}
          </TabBar>
        ) : null}

        {tabsShown && tab === 'incident' ? (
          <ReportIncidentForm
            show={show}
            onHide={onHide}
            userId={userId}
            // Whatever has been answered on the other tab, since it is the same entry being written.
            prefill={{
              productId: formProductId || productId || undefined,
              workday: formWorkday ?? (date ? new Date(date) : undefined),
              today: watch('today') || undefined,
            }}
            onSubmit={onSubmitIncident!}
            bodyClassName={panelBody}
          />
        ) : null}
        <form onSubmit={handleUseFormSubmit(handleSubmit)} hidden={tabsShown && tab === 'incident'}>
          <div className={cn('flex flex-col gap-6', panelBody)}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Controller
                name="productId"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col gap-1.5">
                    <MultiSelectField
                      single
                      clearable={false}
                      filter={matchesLabel}
                      id="report-modal-product"
                      label={t('titles.product')}
                      options={productsOptions}
                      value={selectedValue(field.value)}
                      onChange={syncSelectOnChange(field.onChange)}
                      emptyLabel={isProductsLoading ? '…' : t('select')}
                      // An entry filed on the wrong product is moved here; incident time stays on its product.
                      disabled={!!initialData?.incidentId || !!productId || isProductsLoading}
                      searchPlaceholder={t('searchProduct')}
                      emptySearchLabel={t('noProductsFound')}
                    />
                    {error?.message ? <p className="text-sm text-destructive">{error.message}</p> : null}
                  </div>
                )}
              />
              <Controller
                name="workday"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <WorkdayDateField
                    value={field.value}
                    onChange={field.onChange}
                    open={workdayPickerOpen}
                    onOpenChange={setWorkdayPickerOpen}
                    label={t('titles.workday')}
                    placeholder={t('select')}
                    errorMessage={error?.message}
                    disabledDays={disabledDays}
                  />
                )}
              />
            </div>

            {!userId && (
              <div>
                <Controller
                  name="userId"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="flex flex-col gap-1.5">
                      <MultiSelectField
                        single
                        clearable={false}
                        filter={matchesLabel}
                        id="report-modal-member"
                        label={t('member')}
                        options={memberOptions}
                        value={selectedValue(field.value)}
                        onChange={syncSelectOnChange(field.onChange)}
                        emptyLabel={t('select')}
                        disabled={!!initialData}
                        searchPlaceholder={t('searchMember')}
                        emptySearchLabel={t('noMembersFound')}
                      />
                      {error?.message ? <p className="text-sm text-destructive">{error.message}</p> : null}
                    </div>
                  )}
                />
              </div>
            )}

            <div>
              <Controller
                name="role"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col gap-1.5">
                    {/* The field brings its own label, so this one only heads the message shown instead of it. */}
                    {!showRoles && <Label>{t('titles.involvement')}</Label>}
                    {chaptersIsError && (
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-destructive">{t('chaptersLoadError')}</p>
                        <Button type="button" variant="outline" size="sm" onClick={() => refetchChapters()}>
                          {t('retry')}
                        </Button>
                      </div>
                    )}
                    {!chaptersIsError && !chaptersIsLoading && rolesOptions.length === 0 && (
                      <p className="text-sm text-muted-foreground">{t('noChaptersYet')}</p>
                    )}
                    {showRoles && (
                      <MultiSelectField
                        single
                        clearable={false}
                        filter={matchesLabel}
                        id="report-modal-role"
                        label={t('titles.involvement')}
                        options={rolesOptions}
                        value={selectedValue(field.value)}
                        onChange={syncSelectOnChange(field.onChange)}
                        emptyLabel={chaptersIsLoading ? '…' : t('select')}
                        disabled={!rolesOptions.length || chaptersIsLoading}
                        searchPlaceholder={t('searchInvolvement')}
                        emptySearchLabel={t('noInvolvementsFound')}
                      />
                    )}
                    {error?.message ? <p className="text-sm text-destructive">{error.message}</p> : null}
                  </div>
                )}
              />
            </div>

            <div className={cn('block', errors.hours && 'text-destructive')}>
              <div className="flex flex-col gap-2">
                <p className="text-sm text-foreground">
                  <Trans
                    defaults={t('titles.hoursWithProductName', { product: selectedProductLabel || 'product' })}
                    components={{ bold: <strong /> }}
                  />
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-none sm:gap-2">
                    <Button
                      type="button"
                      className="w-full sm:w-auto"
                      variant={formCustomHours === TimeValueType.fullDay ? 'default' : 'outline'}
                      name="customHours"
                      onClick={() => {
                        setValue('hours', null);
                        clearErrors('hours');
                        setValue('customHours', TimeValueType.fullDay);
                      }}
                    >
                      {t('workingTimeValue.fullDay')}
                    </Button>
                    <Button
                      type="button"
                      className="w-full sm:w-auto"
                      variant={formCustomHours === TimeValueType.halfDay ? 'default' : 'outline'}
                      name="customHours"
                      onClick={() => {
                        setValue('hours', null);
                        clearErrors('hours');
                        setValue('customHours', TimeValueType.halfDay);
                      }}
                    >
                      {t('workingTimeValue.halfDay')}
                    </Button>
                  </div>
                  <span className="w-full px-2 text-center text-sm text-foreground sm:w-auto sm:shrink-0">
                    {t('or')}
                  </span>
                  <div className="w-full min-w-0 flex-1 sm:min-w-[12rem]">
                    <Controller
                      name="hours"
                      control={control}
                      render={({ field }) => (
                        <Input
                          aria-describedby="hours-helper-text"
                          className="w-full"
                          inputMode="decimal"
                          onChange={e => {
                            setValue('customHours', null);
                            const output = e.target.value.replace(/[^0-9.]/g, '');
                            field.onChange(output);
                          }}
                          value={
                            !field.value || Number.isNaN(field.value) || field.value === 0 ? '' : field.value.toString()
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                {errors.hours?.message ? (
                  <p id="hours-helper-text" className="text-sm text-destructive">
                    {errors.hours.message}
                  </p>
                ) : null}
              </div>
            </div>

            <div>
              <Controller
                name="today"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col gap-1.5">
                    {formWorkday instanceof Date && !Number.isNaN(formWorkday.getTime()) ? (
                      <Label htmlFor="report-modal-today">
                        {t('titles.today', {
                          date: shortDate(formWorkday),
                        })}
                      </Label>
                    ) : null}
                    <Textarea
                      {...field}
                      id="report-modal-today"
                      rows={4}
                      disabled={isLoadingSummary}
                      aria-invalid={!!error}
                      className={error ? 'border-destructive' : undefined}
                    />
                    {error?.message ? <p className="text-sm text-destructive">{error.message}</p> : null}
                  </div>
                )}
              />
            </div>

            {/* On a phone: pinned to the sheet's foot, Cancel and Submit side by side under the summary button. */}
            <div className={cn('grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:justify-end', sheetFooterClass)}>
              <Button type="button" variant="outline" className="col-span-2 sm:w-auto" onClick={generateSummary}>
                {t('actions.generateSummaryReport')}
              </Button>
              <Button type="button" variant="outline" className="sm:w-auto" onClick={onHide}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" variant="default" className="sm:w-auto">
                {t('actions.submitReport')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
export default ReportModal;
